import { useEffect, useRef, useState, useMemo, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar, SnackbarContent, CustomContentProps } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Chip,
  OutlinedInput,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  WarningAmber,
  InfoOutlined,
  ArrowBack,
  KeyboardArrowDown,
  Close,
  Check,
} from '@mui/icons-material';
import {
  getApiKeyClaimsBySession,
  updateApiKeyClaimsBySession,
  getWidgetCountries,
  WidgetApiKeyClaimModel,
  WidgetCountryModel,
  getAccessPointTypes,
  AccessPointTypeModel,
} from '../api/magicLinkApi';
import { WidgetClaimType } from '../types.generated';
import useTexts from '../hooks/useTexts';
import DynamicSnackbarMessage from './DynamicSnackbarMessage';
import { getApiDocsUrl, getWidgetLoaderUrl } from '../config';

interface EditApiKeyFormProps {
  onClose: () => void;
  apiKey: string;
  apiKeyCount?: number;
  /** Aktivační stav klíče z BE. Pokud false, řádek "API klíč: ..." v levém
   *  panelu se skryje a tlačítko publish má label "Aktivovat". Po úspěšném
   *  publish se lokálně překlopí na true. */
  isActive?: boolean;
}

interface ClaimsEditorState {
  allowedDomains: string[];
  accessPointTypes: string[];
  defaultLanguage: string;
  viewMode: 'inline' | 'modal';
  selectedCountryCodes: string[];
  defaultCountry: string;
}

const defaultClaimsEditorState: ClaimsEditorState = {
  allowedDomains: [],
  accessPointTypes: [],
  defaultLanguage: 'cs',
  viewMode: 'modal',
  selectedCountryCodes: ['CZ'],
  defaultCountry: 'CZ',
};

// Helper: returns unique type names from the list
const getUniqueTypeNames = (types: AccessPointTypeModel[]): string[] => {
  const seen = new Set<string>();
  return types.reduce<string[]>((acc, t) => {
    if (!seen.has(t.name)) {
      seen.add(t.name);
      acc.push(t.name);
    }
    return acc;
  }, []);
};

const claimsToEditorState = (
  claimsData: WidgetApiKeyClaimModel[],
  availableTypes: AccessPointTypeModel[]
): ClaimsEditorState => {
  const state: ClaimsEditorState = {
    allowedDomains: [],
    // Start with all unique names selected
    accessPointTypes: getUniqueTypeNames(availableTypes),
    defaultLanguage: 'cs',
    viewMode: 'modal',
    selectedCountryCodes: [],
    defaultCountry: '',
  };

  // Collect all disabled values (names or codes) from claims
  const disabledAccessPointValues = new Set<string>();
  claimsData.forEach((claim) => {
    switch (claim.claimType) {
      case WidgetClaimType.AllowedDomain:
        state.allowedDomains.push(claim.claimValue);
        break;
      case WidgetClaimType.DisabledAccessPointType:
        disabledAccessPointValues.add(claim.claimValue);
        break;
      case WidgetClaimType.DefaultLanguage:
        state.defaultLanguage = (
          claim.claimValue.split('-')[0] ?? claim.claimValue
        ).toLowerCase();
        break;
      case WidgetClaimType.ViewMode:
        if (claim.claimValue === 'inline' || claim.claimValue === 'modal') {
          state.viewMode = claim.claimValue;
        }
        break;
      case WidgetClaimType.CountryCode:
        if (!state.selectedCountryCodes.includes(claim.claimValue))
          state.selectedCountryCodes.push(claim.claimValue);
        break;
      case WidgetClaimType.DefaultCountry:
        state.defaultCountry = claim.claimValue;
        break;
    }
  });

  // A name group is disabled if its name is blacklisted
  state.accessPointTypes = state.accessPointTypes.filter(
    (name) => !disabledAccessPointValues.has(name)
  );

  if (state.selectedCountryCodes.length === 0) {
    state.selectedCountryCodes = ['CZ'];
  }

  if (!state.defaultCountry) {
    state.defaultCountry = state.selectedCountryCodes[0] ?? 'CZ';
  }

  return state;
};

const editorStateToClaims = (
  state: ClaimsEditorState,
  availableTypes: AccessPointTypeModel[]
): WidgetApiKeyClaimModel[] => {
  const result: WidgetApiKeyClaimModel[] = [];

  state.allowedDomains.forEach((domain) => {
    if (domain.trim()) {
      result.push({
        claimType: WidgetClaimType.AllowedDomain,
        claimValue: domain.trim(),
      });
    }
  });

  // For each unique name whose name is NOT in the selected names → disabled (blacklist the name)
  getUniqueTypeNames(availableTypes).forEach((name) => {
    if (!state.accessPointTypes.includes(name)) {
      result.push({
        claimType: WidgetClaimType.DisabledAccessPointType,
        claimValue: name,
      });
    }
  });

  result.push({
    claimType: WidgetClaimType.DefaultLanguage,
    claimValue: state.defaultLanguage,
  });
  result.push({
    claimType: WidgetClaimType.ViewMode,
    claimValue: state.viewMode,
  });

  state.selectedCountryCodes.forEach((code) =>
    result.push({ claimType: WidgetClaimType.CountryCode, claimValue: code })
  );

  if (state.defaultCountry) {
    result.push({
      claimType: WidgetClaimType.DefaultCountry,
      claimValue: state.defaultCountry,
    });
  }

  return result;
};

const InfoSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => {
    const { closeSnackbar } = useSnackbar();
    return (
      <SnackbarContent ref={ref}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#fff',
            borderLeft: '6px solid #004B93',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            px: 2,
            py: 1.5,
            width: 525,
          }}
        >
          <InfoOutlined sx={{ color: '#004B93', fontSize: 24, mt: '1px' }} />
          <Typography
            variant="body1"
            sx={{ color: '#1c1c1c', flex: 1, fontWeight: 400 }}
          >
            {message}
          </Typography>
          <Close
            sx={{ fontSize: 24, cursor: 'pointer', mt: '1px', flexShrink: 0 }}
            onClick={() => closeSnackbar(id)}
          />
        </Box>
      </SnackbarContent>
    );
  }
);

const SuccessSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ message }, ref) => {
    return (
      <SnackbarContent ref={ref}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#fff',
            borderLeft: '6px solid #95C620',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            px: 2,
            py: 1.5,
            width: 525,
            paddingRight: 10,
          }}
        >
          <Check sx={{ color: '#95C620', fontSize: 24, mt: '1px' }} />
          <Typography
            variant="body1"
            sx={{
              color: '#1c1c1c',
              flex: 1,
              fontWeight: 400,
              whiteSpace: 'pre-line',
            }}
          >
            {message}
          </Typography>
        </Box>
      </SnackbarContent>
    );
  }
);

const WarningSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => {
    const { closeSnackbar } = useSnackbar();
    return (
      <SnackbarContent ref={ref}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#fff',
            borderLeft: '6px solid #d40511',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            px: 2,
            py: 1.5,
            width: 525,
          }}
        >
          <WarningAmber sx={{ color: '#d40511', fontSize: 24, mt: '1px' }} />
          <Typography
            variant="body1"
            sx={{ color: '#1c1c1c', flex: 1, fontWeight: 400 }}
          >
            {message}
          </Typography>
          <Close
            sx={{ fontSize: 24, cursor: 'pointer', mt: '1px', flexShrink: 0 }}
            onClick={() => closeSnackbar(id)}
          />
        </Box>
      </SnackbarContent>
    );
  }
);

function EditApiKeyForm({
  onClose,
  apiKey,
  apiKeyCount,
  isActive = false,
}: EditApiKeyFormProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const texts = useTexts();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [state, setState] = useState<ClaimsEditorState>(() =>
    structuredClone(defaultClaimsEditorState)
  );
  const [initialState, setInitialState] = useState<ClaimsEditorState>(() =>
    structuredClone(defaultClaimsEditorState)
  );
  const [activeTab, setActiveTab] = useState(0);
  const [mobilePreviewReady, setMobilePreviewReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Mirror BE flagu — start z propu, po úspěšném publish (= aktivaci) přepneme na true.
  const [keyIsActive, setKeyIsActive] = useState<boolean>(isActive);
  const [productType, setProductType] = useState<'custom' | 'smart2box'>(
    'custom'
  );
  const [initialProductType, setInitialProductType] = useState<
    'custom' | 'smart2box'
  >('custom');
  const [domainError, setDomainError] = useState(false);
  const [domainInputError, setDomainInputError] = useState('');
  const [countryError, setCountryError] = useState(false);
  const [defaultCountryError, setDefaultCountryError] = useState(false);
  const [countrySelectOpen, setCountrySelectOpen] = useState(false);
  const [smart2boxTooltipOpen, setSmart2boxTooltipOpen] = useState(false);
  const [codeTabTooltipOpen, setCodeTabTooltipOpen] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<
    WidgetCountryModel[]
  >([]);
  const [availableAccessPointTypes, setAvailableAccessPointTypes] = useState<
    AccessPointTypeModel[]
  >([]);
  const [apTypeError, setApTypeError] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [isDomainInputActive, setIsDomainInputActive] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] =
    useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);
  const isDirtyRef = useRef(false);
  const allowNavigationRef = useRef(false);
  const activationSnackbarKeyRef = useRef<string | number | null>(null);
  const mobileCodeRef = useRef<HTMLDivElement>(null);
  const mobileWidgetContainerRef = useRef<HTMLDivElement>(null);
  const mobileTabsRef = useRef<HTMLDivElement>(null);
  const copyCodeButtonRef = useRef<HTMLDivElement>(null);
  const mobileCopyCodeButtonRef = useRef<HTMLDivElement>(null);

  const isDirty = useMemo(
    () =>
      JSON.stringify(state) !== JSON.stringify(initialState) ||
      productType !== initialProductType,
    [state, initialState, productType, initialProductType]
  );

  // Keep ref in sync for event handler
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Clear success state when user modifies settings
  useEffect(() => {
    if (isDirty && saveSuccess) {
      setSaveSuccess(false);
      setActiveTab(0);
    }
  }, [isDirty, saveSuccess]);

  // Intercept link clicks when dirty
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isDirtyRef.current) return;
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor || !anchor.href) return;
      e.preventDefault();
      setPendingUrl(anchor.href);
      setShowUnsavedDialog(true);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  // Browser close/refresh - native dialog (only option)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      if (allowNavigationRef.current) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Shift+Tab anywhere → focus Domain input
  // Shift+Arrow Left/Right → switch tabs (when both tabs are available)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        setActiveTab(0);
        setIsDomainInputActive(true);
        setTimeout(
          () =>
            domainInputRef.current?.focus({
              focusVisible: true,
            } as FocusOptions),
          0
        );
      }
      if (e.shiftKey && saveSuccess) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setActiveTab(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setActiveTab(0);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [saveSuccess]);

  const previewConfig = useMemo(() => {
    const disabledAccessPointTypes: string[] = getUniqueTypeNames(
      availableAccessPointTypes
    ).filter((name) => !state.accessPointTypes.includes(name));

    return {
      disabledAccessPointTypes,
      countryCodes: state.selectedCountryCodes,
      defaultLanguage: state.defaultLanguage,
      allowedDomains: state.allowedDomains.filter((d) => d.trim()),
      viewMode: 'inline',
    };
  }, [state, availableAccessPointTypes]);

  const previewConfigJson = useMemo(
    () => JSON.stringify(previewConfig),
    [previewConfig]
  );

  const configJson = useMemo(() => JSON.stringify({ mode: 'SHOPCART' }), []);

  // Dynamically load widget loader script
  useEffect(() => {
    const url = getWidgetLoaderUrl();
    if (document.querySelector(`script[src="${url}"]`)) return;

    const script = document.createElement('script');
    script.src = url;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const container = widgetContainerRef.current;
    if (!container || !isDesktop) return;

    const containerHeight = container.clientHeight;
    const heightStyle = containerHeight > 0 ? `${containerHeight}px` : '100%';
    const escapedPreview = previewConfigJson.replace(/'/g, '&apos;');
    const escapedConfig = configJson.replace(/'/g, '&apos;');
    container.innerHTML = `<ppl-access-point-widget id="pplWidgetPreview" api-key="${apiKey}" config='${escapedConfig}' preview-config='${escapedPreview}' style="display:block;width:100%;height:${heightStyle}"></ppl-access-point-widget>`;

    return () => {
      container.innerHTML = '';
    };
  }, [apiKey, previewConfigJson, configJson, activeTab, isDesktop]);

  useEffect(() => {
    const container = mobileWidgetContainerRef.current;
    if (
      !container ||
      isDesktop ||
      !saveSuccess ||
      activeTab !== 0 ||
      !mobilePreviewReady
    )
      return;

    const escapedPreview = previewConfigJson.replace(/'/g, '&apos;');
    const escapedConfig = configJson.replace(/'/g, '&apos;');
    container.innerHTML = `<ppl-access-point-widget id="pplWidgetPreviewMobile" api-key="${apiKey}" config='${escapedConfig}' preview-config='${escapedPreview}' style="display:block;width:100%;height:688px"></ppl-access-point-widget>`;

    return () => {
      container.innerHTML = '';
    };
  }, [
    apiKey,
    previewConfigJson,
    configJson,
    activeTab,
    isDesktop,
    saveSuccess,
    mobilePreviewReady,
  ]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [claimsData, countriesData, apTypesData] = await Promise.all([
          getApiKeyClaimsBySession(apiKey),
          getWidgetCountries(),
          getAccessPointTypes(),
        ]);
        const loaded = claimsToEditorState(claimsData, apTypesData);
        setState(loaded);
        setInitialState(loaded);
        setAvailableCountries(countriesData);
        setAvailableAccessPointTypes(apTypesData);

        const smart2BoxClaim = claimsData.find(
          (c) => c.claimType === WidgetClaimType.Smart2Box
        );
        const detectedType =
          smart2BoxClaim?.claimValue === 'true' ? 'smart2box' : 'custom';
        setProductType(detectedType);
        setInitialProductType(detectedType);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        if (errorMessage === 'SESSION_EXPIRED') {
          setShowSessionExpiredDialog(true);
        } else {
          enqueueSnackbar(
            <DynamicSnackbarMessage textKey="ERR_LOAD_API_KEY" />,
            {
              variant: 'warning',
            }
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [apiKey, texts]);

  const isValidDomain = (domain: string) => {
    // Wildcard '*' is reserved for internal use and cannot be entered by users
    if (domain === '*') return false;
    // Allow localhost with optional port (e.g. localhost, localhost:3000)
    if (/^localhost(:\d{1,5})?$/.test(domain)) return true;
    const base = domain.startsWith('*.') ? domain.slice(2) : domain;
    // Strukturální kontrola: labely alfanumerické (vnitřní pomlčky OK),
    // TLD alespoň 2 písmena. Konkrétní TLD nefiltrujeme — nových gTLD
    // přibývá pořád (např. .coffee), whitelist je neudržitelný.
    return /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(
      base
    );
  };

  const handleAddDomain = () => {
    const val = domainInput.trim();
    if (!val) return;
    if (!isValidDomain(val)) {
      setDomainInputError(texts.ERR_DOMAIN_INVALID);
      return;
    }
    setDomainInputError('');
    setState((prev) => ({
      ...prev,
      allowedDomains: [...prev.allowedDomains, val],
    }));
    setDomainInput('');
    setTimeout(() => domainInputRef.current?.focus(), 0);
  };

  const handleActivateDomainInput = () => {
    if (!isDomainInputActive) {
      setIsDomainInputActive(true);
      setTimeout(() => domainInputRef.current?.focus(), 0);
    } else if (domainInput.trim()) {
      handleAddDomain();
    } else {
      domainInputRef.current?.focus();
    }
  };

  const handleRemoveDomain = (index: number) => {
    setState((prev) => ({
      ...prev,
      allowedDomains: prev.allowedDomains.filter((_, i) => i !== index),
    }));
  };

  const handleAccessPointTypeChange = (typeName: string, checked: boolean) => {
    setApTypeError(false);
    setState((prev) => ({
      ...prev,
      accessPointTypes: checked
        ? [...prev.accessPointTypes, typeName]
        : prev.accessPointTypes.filter((name) => name !== typeName),
    }));
  };

  const handleProductTypeChange = (type: 'custom' | 'smart2box') => {
    setProductType(type);
    setApTypeError(false);
    if (type === 'smart2box') {
      setState((prev) => ({
        ...prev,
        accessPointTypes: getUniqueTypeNames(
          availableAccessPointTypes.filter((t) => t.smartToBox)
        ),
        selectedCountryCodes: ['CZ'],
        defaultCountry: 'CZ',
      }));
      setCountryError(false);
      setDefaultCountryError(false);
    }
  };

  const countryNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    availableCountries.forEach((c) => {
      map[c.countryCode] =
        c.names.find((n) => n.language === 'cs')?.name ??
        c.names[0]?.name ??
        c.countryCode;
    });
    return map;
  }, [availableCountries]);

  const handleCountriesChange = (codes: string[]) => {
    setCountryError(false);
    setState((prev) => ({
      ...prev,
      selectedCountryCodes: codes,
      defaultCountry: codes.includes(prev.defaultCountry)
        ? prev.defaultCountry
        : '',
    }));
  };

  const handleReset = () => {
    const defaults = structuredClone(defaultClaimsEditorState);
    setState(defaults);
    setSaveSuccess(false);
    setDomainError(false);
    setDomainInputError('');
    setDomainInput('');
    setIsDomainInputActive(false);
    setCountryError(false);
    setDefaultCountryError(false);
    setApTypeError(false);
    setProductType('custom');
  };

  const handlePublish = async () => {
    closeSnackbar();
    activationSnackbarKeyRef.current = null;
    setSaveSuccess(false);
    setDomainError(false);
    setDomainInputError('');
    setCountryError(false);
    setDefaultCountryError(false);
    setApTypeError(false);

    // Auto-add pending domain input before validation
    let currentDomains = state.allowedDomains;
    if (domainInput.trim()) {
      const pending = domainInput.trim();
      if (!isValidDomain(pending)) {
        setDomainInputError(texts.ERR_DOMAIN_INVALID);
        return;
      }
      currentDomains = [...currentDomains, pending];
      setState((prev) => ({
        ...prev,
        allowedDomains: currentDomains,
      }));
      setDomainInput('');
      setIsDomainInputActive(false);
    }

    let hasError = false;
    let firstErrorField: string | null = null;

    if (currentDomains.length === 0) {
      setDomainError(true);
      hasError = true;
      firstErrorField ??= 'domain';
    } else if (currentDomains.some((domain) => !isValidDomain(domain.trim()))) {
      setDomainError(true);
      setDomainInputError(texts.ERR_DOMAIN_INVALID);
      hasError = true;
      firstErrorField ??= 'domain';
    }

    if (productType === 'custom') {
      if (state.accessPointTypes.length === 0) {
        setApTypeError(true);
        hasError = true;
        firstErrorField ??= 'apType';
      }
    }

    if (state.selectedCountryCodes.length === 0) {
      setCountryError(true);
      hasError = true;
      firstErrorField ??= 'country';
    }

    if (!state.defaultCountry) {
      setDefaultCountryError(true);
      hasError = true;
      firstErrorField ??= 'defaultCountry';
    }

    if (hasError) {
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${firstErrorField}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    const stateToSave = { ...state, allowedDomains: currentDomains };
    const claimsToSave = editorStateToClaims(
      stateToSave,
      availableAccessPointTypes
    );
    claimsToSave.push({
      claimType: WidgetClaimType.Smart2Box,
      claimValue: productType === 'smart2box' ? 'true' : 'false',
    });
    setIsLoading(true);

    try {
      await updateApiKeyClaimsBySession(apiKey, claimsToSave);
      setSaveSuccess(true);
      setKeyIsActive(true);
      setInitialState(stateToSave);
      setInitialProductType(productType);
      setActiveTab(1);
      setTimeout(() => {
        if (isDesktop) {
          copyCodeButtonRef.current?.focus({
            focusVisible: true,
          } as FocusOptions);
        } else {
          mobileCodeRef.current?.scrollIntoView({ behavior: 'smooth' });
          mobileCopyCodeButtonRef.current?.focus({
            focusVisible: true,
          } as FocusOptions);
        }
      }, 100);
      if (isDesktop) {
        enqueueSnackbar(<DynamicSnackbarMessage textKey="CHANGES_SAVED" />, {
          variant: 'success',
          autoHideDuration: 5000,
        });
        activationSnackbarKeyRef.current = enqueueSnackbar(
          <DynamicSnackbarMessage textKey="ACTIVATION_SNACKBAR" />,
          {
            variant: 'info',
            persist: true,
          }
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage === 'SESSION_EXPIRED') {
        setShowSessionExpiredDialog(true);
      } else {
        enqueueSnackbar(<DynamicSnackbarMessage textKey="ERR_SAVE_FAILED" />, {
          variant: 'warning',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', pt: { xs: 0, md: 2 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {apiKeyCount != null && apiKeyCount > 1 && (
              <ArrowBack
                onClick={() => {
                  closeSnackbar();
                  onClose();
                }}
                sx={{
                  fontSize: 24,
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              />
            )}
            <Typography variant="h4">{texts.APP_TITLE}</Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {texts.EDIT_API_KEY_DESC}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              my: 1.5,
            }}
          >
            <InfoOutlined
              sx={{
                fontSize: 24,
                mr: 0.5,
                mt: -0.5,
                color: 'primary.main',
                boxSizing: 'content-box',
              }}
            />
            <Typography variant="body1">
              <Typography
                component="span"
                variant="body1"
                sx={{ display: { xs: 'none', md: 'inline' } }}
              >
                {texts.HELP_TEXT}{' '}
              </Typography>
              <Typography
                component="span"
                variant="body1"
                sx={{ display: { xs: 'inline', md: 'none' } }}
              >
                {texts.HELP_TEXT_MOBILE}{' '}
              </Typography>
              <Typography
                component="a"
                variant="body1"
                href={getApiDocsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                {texts.HELP_LINK_TEXT}
              </Typography>
              .
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex' }}>
          {[texts.PREVIEW, texts.WIDGET_CODE].map((label, index) => {
            const isDisabled = index === 1 && !saveSuccess;
            return (
              <Tooltip
                key={label}
                title={isDisabled ? texts.WIDGET_CODE_TAB_TOOLTIP : ''}
                arrow={isDisabled}
                placement="bottom"
                {...(!isDesktop && isDisabled
                  ? {
                      open: codeTabTooltipOpen,
                      disableHoverListener: true,
                      disableFocusListener: true,
                      disableTouchListener: true,
                    }
                  : {})}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  },
                  tooltip: {
                    sx: {
                      bgcolor: '#004B93',
                      color: '#fff',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      minWidth: 444,
                      p: '20px 16px',
                      borderRadius: 0,
                      right: 16,
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#004B93',
                      fontSize: 20,
                    },
                  },
                }}
              >
                <Box
                  onClick={() => {
                    if (!isDesktop && isDisabled) {
                      setCodeTabTooltipOpen((prev) => !prev);
                    } else if (!isDisabled) {
                      setActiveTab(index);
                    }
                  }}
                  sx={{
                    width: 160,
                    height: 32,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    bgcolor:
                      activeTab === index ? 'common.white' : 'transparent',
                    cursor: isDisabled ? 'default' : 'pointer',
                    pb: 0.5,
                    zIndex: 10,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Grid
        container
        spacing={{ xs: 0, md: 2 }}
        sx={{
          height: { xs: '100%', md: 741 },
        }}
      >
        {/* Left Column: Settings */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              p: 0,
              height: { xs: 'auto', md: '100%' },
              display: 'flex',
              flexDirection: 'column',
              ml: { xs: -2, md: 0 },
              mr: { xs: -2, md: 0 },
              overflow: { xs: 'visible', md: 'hidden' },
            }}
          >
            {/* Pinned Title */}
            <Typography
              variant="h5"
              color="primary"
              sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}
            >
              {texts.WIDGET_SETTINGS}
            </Typography>

            {/* Scrollable Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: { xs: 'visible', md: 'auto' },
                px: 2,
                minHeight: 0,
              }}
            >
              {keyIsActive && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                    mb: 3,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
                    {texts.API_KEY}:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {apiKey}
                  </Typography>
                  <IconButton
                    size="small"
                    title={texts.COPY_API_KEY}
                    onClick={() => {
                      navigator.clipboard.writeText(apiKey);
                      enqueueSnackbar(
                        <DynamicSnackbarMessage textKey="API_KEY_COPIED" />,
                        {
                          variant: 'info',
                          autoHideDuration: 3000,
                        }
                      );
                    }}
                    sx={{
                      flexShrink: 0,
                      ml: 'auto',
                      width: 40,
                      height: 40,
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* 1. Domains */}
              <Box
                data-field="domain"
                sx={{
                  ...((domainError || domainInputError) && {
                    bgcolor: (t: any) => t.custom.errorBg,
                    borderRadius: 0,
                    p: 2,
                    mx: -2,
                  }),
                }}
              >
                <Typography variant="h5" sx={{ mb: 1, fontSize: 16 }}>
                  {texts.ADD_DOMAIN_LABEL}{' '}
                  <Typography component="span" sx={{ color: 'error.main' }}>
                    *
                  </Typography>
                </Typography>
                <Box
                  tabIndex={isDomainInputActive ? -1 : 0}
                  onClick={() => {
                    if (!isLoading) {
                      if (!isDomainInputActive) {
                        setIsDomainInputActive(true);
                        setTimeout(() => domainInputRef.current?.focus(), 0);
                      } else {
                        domainInputRef.current?.focus();
                      }
                    }
                  }}
                  onFocus={(e) => {
                    if (
                      !isLoading &&
                      !isDomainInputActive &&
                      e.target === e.currentTarget
                    ) {
                      setIsDomainInputActive(true);
                      setTimeout(() => domainInputRef.current?.focus(), 0);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    py: '8px',
                    px: '10px',
                    mb: 0.5,
                    bgcolor: 'common.white',
                    borderRadius: '4px',
                    border: (t: any) =>
                      `1px solid ${domainError || domainInputError ? t.palette.error.main : t.custom.borderDefault}`,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    cursor: !isLoading ? 'text' : 'default',
                    '&:hover': {
                      borderWidth: 1,
                      borderColor: (t: any) =>
                        domainError || domainInputError
                          ? t.palette.error.main
                          : t.custom.borderDefault,
                    },
                    '&:focus-within, &:focus-visible': {
                      borderWidth: 2,
                      borderColor: (t: any) =>
                        domainError || domainInputError
                          ? t.palette.error.main
                          : t.custom.borderFocused,
                    },
                  }}
                >
                  {isDomainInputActive && (
                    <TextField
                      variant="standard"
                      size="small"
                      inputRef={domainInputRef}
                      disabled={isLoading}
                      value={domainInput}
                      onChange={(e) => {
                        setDomainInput(e.target.value);
                        setDomainError(false);
                        setDomainInputError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDomain();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setDomainInput('');
                          setDomainInputError('');
                          setIsDomainInputActive(false);
                        }
                      }}
                      onBlur={() => {
                        const val = domainInput.trim();
                        if (val) {
                          if (isValidDomain(val)) {
                            setDomainInputError('');
                            setState((prev) => ({
                              ...prev,
                              allowedDomains: [...prev.allowedDomains, val],
                            }));
                            setDomainInput('');
                            setIsDomainInputActive(false);
                          }
                        } else {
                          setIsDomainInputActive(false);
                          setDomainInputError('');
                        }
                      }}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        flex: '1 1 100%',
                      }}
                    />
                  )}
                  {state.allowedDomains.map((domain, index) => (
                    <Chip
                      key={index}
                      label={domain || texts.DOMAIN_PLACEHOLDER}
                      onDelete={() => handleRemoveDomain(index)}
                      deleteIcon={<Close sx={{ fontSize: 18 }} />}
                      size="small"
                      sx={{
                        bgcolor: (t: any) => t.custom.chipBg,
                        borderRadius: '4px',
                        pl: 1,
                        pr: 0,
                        color: (t: any) => t.custom.textMuted,
                        '& .MuiChip-deleteIcon': {
                          color: (t: any) => t.custom.textMuted,
                          fontSize: 24,
                        },
                      }}
                    />
                  ))}
                </Box>
                {domainError && (
                  <Typography
                    sx={{
                      color: 'error.dark',
                      fontSize: '0.875rem',
                      mt: 0.5,
                    }}
                  >
                    {texts.ERR_REQUIRED_FIELD}
                  </Typography>
                )}
                {domainInputError && (
                  <Typography
                    sx={{
                      color: 'error.dark',
                      fontSize: '0.875rem',
                      mt: 0.5,
                    }}
                  >
                    {domainInputError}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="subtitle1"
                component="span"
                onClick={!isLoading ? handleActivateDomainInput : undefined}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                  mb: 3,
                  color: !isLoading ? 'primary.main' : 'action.disabled',
                  cursor: !isLoading ? 'pointer' : 'default',
                  '&:hover': !isLoading ? { textDecoration: 'underline' } : {},
                }}
              >
                <Add sx={{ fontSize: 24 }} />
                {texts.ADD_NEXT}
              </Typography>

              {/* 2. Product Type */}
              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <Typography variant="h5" sx={{ mb: 1, fontSize: 16 }}>
                    {texts.PRODUCT_TYPE}
                  </Typography>
                  <RadioGroup
                    name="product-type"
                    value={productType}
                    onChange={(e) =>
                      handleProductTypeChange(
                        e.target.value as 'custom' | 'smart2box'
                      )
                    }
                    sx={{ ml: 2.5 }}
                  >
                    <FormControlLabel
                      value="custom"
                      control={<Radio sx={{ mr: 1 }} />}
                      label={
                        <Typography variant="subtitle1">
                          {texts.PRODUCT_CUSTOM}
                        </Typography>
                      }
                      disabled={isLoading}
                      sx={{ height: 44 }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        height: 44,
                      }}
                    >
                      <FormControlLabel
                        value="smart2box"
                        control={<Radio sx={{ mr: 1 }} />}
                        label={
                          <Typography variant="subtitle1">
                            {texts.PRODUCT_SMART2BOX}
                          </Typography>
                        }
                        disabled={isLoading}
                        sx={{ height: 44, flex: 1, mr: 0 }}
                      />
                      <Tooltip
                        title={texts.PRODUCT_SMART2BOX_TOOLTIP}
                        arrow
                        placement="top"
                        {...(!isDesktop
                          ? {
                              open: smart2boxTooltipOpen,
                              disableHoverListener: true,
                              disableFocusListener: true,
                              disableTouchListener: true,
                            }
                          : {})}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, isDesktop ? 12 : 4],
                                },
                              },
                            ],
                          },
                          tooltip: {
                            sx: {
                              bgcolor: '#004B93',
                              color: '#fff',
                              fontWeight: 400,
                              fontSize: '18px',
                              lineHeight: '25px',
                              minWidth: 444,
                              p: '20px 16px',
                              borderRadius: 0,
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#004B93',
                              fontSize: 20,
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (!isDesktop) {
                              setSmart2boxTooltipOpen((prev) => !prev);
                            }
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            color: 'primary.main',
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* 3. Access Point Types */}
              <Box
                data-field="apType"
                sx={{
                  mb: 3,
                  ...(apTypeError && {
                    bgcolor: (t: any) => t.custom.errorBg,
                    borderRadius: 0,
                    p: 2,
                    mx: -2,
                  }),
                }}
              >
                <Typography variant="h5" sx={{ mb: 1, fontSize: 16 }}>
                  {texts.ACCESS_POINT_TYPES_LABEL}{' '}
                  <Typography component="span" sx={{ color: 'error.main' }}>
                    *
                  </Typography>
                </Typography>
                <Box ml={2.5} sx={{ display: 'flex', flexDirection: 'column' }}>
                  {getUniqueTypeNames(availableAccessPointTypes).map(
                    (typeName) => {
                      const disabled = isLoading || productType === 'smart2box';
                      return (
                        <FormControlLabel
                          key={typeName}
                          control={
                            <Checkbox
                              checked={state.accessPointTypes.includes(
                                typeName
                              )}
                              onChange={(e) =>
                                handleAccessPointTypeChange(
                                  typeName,
                                  e.target.checked
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAccessPointTypeChange(
                                    typeName,
                                    !state.accessPointTypes.includes(typeName)
                                  );
                                }
                              }}
                              color="primary"
                              disabled={disabled}
                              sx={{ mr: 1, height: 44 }}
                            />
                          }
                          label={
                            <Typography
                              variant="subtitle1"
                              color={
                                disabled ? 'text.disabled' : 'text.primary'
                              }
                            >
                              {typeName}
                            </Typography>
                          }
                        />
                      );
                    }
                  )}
                </Box>
                {apTypeError && (
                  <Typography
                    sx={{
                      color: 'error.dark',
                      fontSize: '0.875rem',
                      mt: 0.5,
                    }}
                  >
                    {texts.ERR_REQUIRED_FIELD}
                  </Typography>
                )}
              </Box>

              {/* 4. Available Countries (multi-select dropdown) */}
              <Box
                data-field="country"
                sx={{
                  mb: 3,
                  ...(countryError && {
                    bgcolor: (t: any) => t.custom.errorBg,
                    borderRadius: 0,
                    p: 2,
                    mx: -2,
                  }),
                }}
              >
                <FormControl component="fieldset" fullWidth>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 1,
                      fontSize: 16,
                      color:
                        productType === 'smart2box'
                          ? 'text.disabled'
                          : undefined,
                    }}
                  >
                    {texts.AVAILABLE_COUNTRIES}{' '}
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      *
                    </Typography>
                  </Typography>
                  <Select
                    multiple
                    open={countrySelectOpen}
                    onOpen={() => setCountrySelectOpen(true)}
                    onClose={() => setCountrySelectOpen(false)}
                    IconComponent={({ className }) => (
                      <KeyboardArrowDown
                        className={className}
                        onMouseDown={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setCountrySelectOpen((prev) => !prev);
                        }}
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                      />
                    )}
                    MenuProps={{}}
                    value={state.selectedCountryCodes}
                    error={countryError}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      if (value.includes('__all__')) {
                        const allCodes = availableCountries.map(
                          (c) => c.countryCode
                        );
                        handleCountriesChange(
                          state.selectedCountryCodes.length ===
                            availableCountries.length
                            ? []
                            : allCodes
                        );
                      } else {
                        handleCountriesChange(value);
                      }
                    }}
                    input={
                      <OutlinedInput
                        error={countryError}
                        endAdornment={
                          state.selectedCountryCodes.length > 0 &&
                          !isLoading &&
                          productType !== 'smart2box' ? (
                            <Box
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleCountriesChange([]);
                              }}
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: '#515151',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                mr: 3,
                                flexShrink: 0,
                              }}
                            >
                              <Close sx={{ fontSize: 16, color: '#fff' }} />
                            </Box>
                          ) : null
                        }
                      />
                    }
                    renderValue={(selected) => {
                      const sel = selected as string[];
                      const text =
                        sel.length === 1
                          ? (countryNameMap[sel[0]!] ?? sel[0])
                          : `${texts.SELECTED_COUNT} [ ${sel.length} ]`;
                      return <Typography variant="body2">{text}</Typography>;
                    }}
                    disabled={isLoading || productType === 'smart2box'}
                    size="small"
                    sx={{
                      borderRadius: '4px',
                      bgcolor: 'common.white',
                    }}
                  >
                    <MenuItem
                      value="__all__"
                      sx={{
                        borderBottom: (t: any) =>
                          `1px solid ${t.custom.borderDefault}`,
                        pl: 1,
                      }}
                    >
                      <Checkbox
                        checked={
                          state.selectedCountryCodes.length ===
                          availableCountries.length
                        }
                        indeterminate={
                          state.selectedCountryCodes.length > 0 &&
                          state.selectedCountryCodes.length <
                            availableCountries.length
                        }
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        {texts.SELECT_ALL}
                      </Typography>
                    </MenuItem>
                    {availableCountries.map((country) => (
                      <MenuItem
                        key={country.countryCode}
                        value={country.countryCode}
                        sx={{ pl: 1 }}
                      >
                        <Checkbox
                          checked={state.selectedCountryCodes.includes(
                            country.countryCode
                          )}
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">
                          {countryNameMap[country.countryCode] ??
                            country.countryCode}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {countryError && (
                    <Typography
                      sx={{
                        color: 'error.dark',
                        fontSize: '0.875rem',
                        mt: 0.5,
                      }}
                    >
                      {texts.ERR_REQUIRED_FIELD}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              {/* 5. Default Country */}
              <Box
                data-field="defaultCountry"
                sx={{
                  mb: 3,
                  ...(defaultCountryError && {
                    bgcolor: (t: any) => t.custom.errorBg,
                    borderRadius: 0,
                    p: 2,
                    mx: -2,
                  }),
                }}
              >
                <FormControl component="fieldset" fullWidth>
                  <Typography
                    variant="h5"
                    sx={{ mb: 1, fontSize: 16 }}
                    color={
                      productType === 'smart2box' ||
                      state.selectedCountryCodes.length === 0
                        ? 'text.disabled'
                        : 'text.primary'
                    }
                  >
                    {texts.DEFAULT_WIDGET_COUNTRY}{' '}
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      *
                    </Typography>
                  </Typography>
                  <Select
                    value={state.defaultCountry}
                    onChange={(e) => {
                      setDefaultCountryError(false);
                      setState((prev) => ({
                        ...prev,
                        defaultCountry: e.target.value,
                      }));
                    }}
                    error={defaultCountryError}
                    displayEmpty
                    disabled={
                      isLoading ||
                      productType === 'smart2box' ||
                      state.selectedCountryCodes.length === 0
                    }
                    size="small"
                    sx={{ borderRadius: '4px', bgcolor: 'common.white' }}
                  >
                    {state.selectedCountryCodes.map((code) => (
                      <MenuItem key={code} value={code} sx={{ height: 48 }}>
                        <Typography variant="body2">
                          {countryNameMap[code] ?? code}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {defaultCountryError && (
                    <Typography
                      sx={{
                        color: 'error.dark',
                        fontSize: '0.875rem',
                        mt: 0.5,
                      }}
                    >
                      {texts.ERR_REQUIRED_FIELD}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              {/* 6. Default Language */}
              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <Typography variant="h5" sx={{ mb: 1, fontSize: 16 }}>
                    {texts.DEFAULT_LANGUAGE}
                  </Typography>
                  <Select
                    value={state.defaultLanguage}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        defaultLanguage: e.target.value,
                      }))
                    }
                    disabled={isLoading}
                    size="small"
                  >
                    <MenuItem value="cs" sx={{ height: 48 }}>
                      <Typography variant="body2">{texts.LANG_CS}</Typography>
                    </MenuItem>
                    <MenuItem value="en" sx={{ height: 48 }}>
                      <Typography variant="body2">{texts.LANG_EN}</Typography>
                    </MenuItem>
                    <MenuItem value="pl" sx={{ height: 48 }}>
                      <Typography variant="body2">{texts.LANG_PL}</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 6. View Mode */}
              <Box sx={{ mb: 5 }}>
                <FormControl component="fieldset">
                  <Typography variant="h5" sx={{ mb: 1, fontSize: 16 }}>
                    {texts.VIEW_MODE_LABEL}
                  </Typography>
                  <RadioGroup
                    name="view-mode"
                    value={state.viewMode}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        viewMode: e.target.value as 'inline' | 'modal',
                      }))
                    }
                    sx={{ ml: 2.5 }}
                  >
                    <FormControlLabel
                      value="modal"
                      control={<Radio sx={{ mr: 1 }} />}
                      label={
                        <Typography variant="subtitle1">
                          {texts.VIEW_MODE_MODAL}
                        </Typography>
                      }
                      disabled={isLoading}
                      sx={{ height: 44 }}
                    />
                    <FormControlLabel
                      value="inline"
                      control={<Radio sx={{ mr: 1 }} />}
                      label={
                        <Typography variant="subtitle1">
                          {texts.VIEW_MODE_INLINE}
                        </Typography>
                      }
                      disabled={isLoading}
                      sx={{ height: 44 }}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
            {/* End Scrollable Content */}

            {/* Pinned Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                px: 2,
                pb: 2,
                pt: 2,
                flexShrink: 0,
                position: { xs: 'sticky', md: 'static' },
                bottom: { xs: 0, md: 'auto' },
                bgcolor: { xs: 'background.paper', md: 'transparent' },
                zIndex: { xs: 10, md: 'auto' },
                boxShadow: { xs: '0 -2px 8px rgba(0,0,0,0.1)', md: 'none' },
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={handleReset}
                disabled={isLoading}
                sx={{
                  height: 49,
                  borderRadius: '4px',
                  color: 'primary.main',
                  borderColor: (t: any) => t.custom.outlinedBorder,
                }}
              >
                {texts.RESET}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handlePublish}
                disabled={isLoading || saveSuccess}
                sx={{
                  height: 49,
                  borderRadius: '4px',
                  ...(!saveSuccess &&
                    !isLoading && { bgcolor: 'primary.main' }),
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> {texts.SAVING}
                  </>
                ) : keyIsActive ? (
                  texts.SAVE_CHANGES
                ) : (
                  texts.PUBLISH
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Preview / Widget Code */}
        <Grid
          item
          xs={12}
          md={9}
          sx={{ height: '100%', display: { xs: 'none', md: 'block' } }}
        >
          {activeTab === 0 ? (
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <div
                ref={widgetContainerRef}
                style={{ width: '100%', height: '100%', position: 'relative' }}
              />
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  bgcolor: (t: any) => t.custom.chipBg,
                  color: (t: any) => t.custom.textBody,
                  px: 2,
                  py: 2,
                  m: 2,
                  overflow: 'auto',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontSize: 16,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5,
                    bgcolor: (t: any) => t.custom.chipBg,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                >
                  <Box
                    ref={copyCodeButtonRef}
                    tabIndex={0}
                    role="button"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                      '&:focus-visible': {
                        outline: '2px solid #004B93',
                        outlineOffset: 2,
                        borderRadius: '4px',
                      },
                      m: -2,
                    }}
                    onClick={() => {
                      const widgetElement = `<!-- ${texts.WIDGET_CODE_COMMENT_ELEMENT} -->\n<ppl-access-point-widget\n  id="pplWidget"\n  api-key="${apiKey}"\n></ppl-access-point-widget>`;
                      const modalCode =
                        state.viewMode === 'modal'
                          ? `\n\n<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_ONCLICK} -->\n<button onclick="document.getElementById('pplWidget').open()">\n  ${texts.WIDGET_CODE_BUTTON_TEXT}\n</button>\n\n<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_EVENT} -->\n<script>\n  document.getElementById('openPplWidget').addEventListener('click', function() {\n    document.getElementById('pplWidget').open();\n  });\n</script>`
                          : '';
                      const code = `<!-- ${texts.WIDGET_CODE_COMMENT_SCRIPT} -->\n<script src="${getWidgetLoaderUrl()}"></script>\n\n${widgetElement}${modalCode}`;
                      navigator.clipboard.writeText(code);
                      enqueueSnackbar(
                        <DynamicSnackbarMessage textKey="CODE_COPIED" />,
                        {
                          variant: 'info',
                          autoHideDuration: 3000,
                        }
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.main', fontWeight: 700 }}
                    >
                      {texts.COPY_CODE}
                    </Typography>
                    <ContentCopy
                      sx={{ color: 'primary.main' }}
                      fontSize="small"
                    />
                  </Box>
                </Box>
                {`<!-- ${texts.WIDGET_CODE_COMMENT_SCRIPT} -->
<script src="${getWidgetLoaderUrl()}"></script>

<!-- ${texts.WIDGET_CODE_COMMENT_ELEMENT} -->
<ppl-access-point-widget
  id="pplWidget"
  api-key="${apiKey}"
></ppl-access-point-widget>${
                  state.viewMode === 'modal'
                    ? `

<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_ONCLICK} -->
<button onclick="document.getElementById('pplWidget').open()">
  ${texts.WIDGET_CODE_BUTTON_TEXT}
</button>

<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_EVENT} -->
<script>
  document.getElementById('openPplWidget').addEventListener('click', function() {
    document.getElementById('pplWidget').open();
  });
</script>`
                    : ''
                }`}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Mobile: success box + tabs + preview/code */}
      {saveSuccess && (
        <Box
          ref={mobileCodeRef}
          sx={{
            display: { xs: 'block', md: 'none' },
            mt: 2,
          }}
        >
          {/* Success info box */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              bgcolor: '#ffffff',
              borderLeft: '6px solid #004B93',
              p: 2,
              mb: 5,
            }}
          >
            <InfoOutlined
              sx={{ color: '#004B93', fontSize: 24, flexShrink: 0 }}
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {texts.CHANGES_SAVED}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {texts.ACTIVATION_SNACKBAR}
              </Typography>
            </Box>
          </Box>

          {/* Backdrop for preview tab */}
          {activeTab === 0 && mobilePreviewReady && (
            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 10,
              }}
            />
          )}

          {/* Tabs */}
          <Box
            ref={mobileTabsRef}
            sx={{ display: 'flex', position: 'relative', zIndex: 11, mx: -2 }}
          >
            {[texts.PREVIEW, texts.WIDGET_CODE].map((label, index) => (
              <Box
                key={label}
                onClick={() => {
                  if (index === 0) {
                    setMobilePreviewReady(false);
                    setActiveTab(index);
                    // Add temporary spacer so scroll target is reachable
                    const spacer = document.createElement('div');
                    spacer.style.height = '100vh';
                    document.body.appendChild(spacer);
                    const tabsEl = mobileTabsRef.current;
                    if (tabsEl) {
                      const top =
                        tabsEl.getBoundingClientRect().top +
                        window.scrollY -
                        75;
                      window.scrollTo({ top, behavior: 'smooth' });
                    }
                    setTimeout(() => {
                      spacer.remove();
                      setMobilePreviewReady(true);
                    }, 500);
                  } else {
                    setMobilePreviewReady(false);
                    setActiveTab(index);
                  }
                }}
                sx={{
                  px: 3,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                  bgcolor: activeTab === index ? 'common.white' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Tab content */}
          {activeTab === 0 ? (
            mobilePreviewReady ? (
              <Paper
                elevation={0}
                sx={{
                  minHeight: 688,
                  overflow: 'hidden',
                  position: 'relative',
                  zIndex: 11,
                  mx: -2,
                  borderRadius: 0,
                }}
              >
                <div
                  ref={mobileWidgetContainerRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                  }}
                />
              </Paper>
            ) : null
          ) : (
            <Paper
              elevation={0}
              sx={{
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                mx: -2,
              }}
            >
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  bgcolor: 'transparent',
                  color: (t: any) => t.custom.textBody,
                  p: 3,
                  pb: 6,
                  m: 0,
                  overflow: 'auto',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontSize: 14,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    px: 2.5,
                    pt: 1.5,
                    pb: 3,
                    bgcolor: 'transparent',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                >
                  <Box
                    ref={mobileCopyCodeButtonRef}
                    tabIndex={0}
                    role="button"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                      '&:focus-visible': {
                        outline: '2px solid #004B93',
                        outlineOffset: 2,
                        borderRadius: '4px',
                      },
                      m: -2,
                    }}
                    onClick={() => {
                      const widgetElement = `<!-- ${texts.WIDGET_CODE_COMMENT_ELEMENT} -->\n<ppl-access-point-widget\n  id="pplWidget"\n  api-key="${apiKey}"\n></ppl-access-point-widget>`;
                      const modalCode =
                        state.viewMode === 'modal'
                          ? `\n\n<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_ONCLICK} -->\n<button onclick="document.getElementById('pplWidget').open()">\n  ${texts.WIDGET_CODE_BUTTON_TEXT}\n</button>\n\n<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_EVENT} -->\n<script>\n  document.getElementById('openPplWidget').addEventListener('click', function() {\n    document.getElementById('pplWidget').open();\n  });\n</script>`
                          : '';
                      const code = `<!-- ${texts.WIDGET_CODE_COMMENT_SCRIPT} -->\n<script src="${getWidgetLoaderUrl()}"></script>\n\n${widgetElement}${modalCode}`;
                      navigator.clipboard.writeText(code);
                      enqueueSnackbar(
                        <DynamicSnackbarMessage textKey="CODE_COPIED" />,
                        {
                          variant: 'info',
                          autoHideDuration: 3000,
                        }
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.main', fontWeight: 700 }}
                    >
                      {texts.COPY_CODE}
                    </Typography>
                    <ContentCopy
                      sx={{ color: 'primary.main' }}
                      fontSize="small"
                    />
                  </Box>
                </Box>
                {`<!-- ${texts.WIDGET_CODE_COMMENT_SCRIPT} -->
<script src="${getWidgetLoaderUrl()}"></script>

<!-- ${texts.WIDGET_CODE_COMMENT_ELEMENT} -->
<ppl-access-point-widget
  id="pplWidget"
  api-key="${apiKey}"
></ppl-access-point-widget>${
                  state.viewMode === 'modal'
                    ? `

<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_ONCLICK} -->
<button onclick="document.getElementById('pplWidget').open()">
  ${texts.WIDGET_CODE_BUTTON_TEXT}
</button>

<!-- ${texts.WIDGET_CODE_COMMENT_BUTTON_EVENT} -->
<script>
  document.getElementById('openPplWidget').addEventListener('click', function() {
    document.getElementById('pplWidget').open();
  });
</script>`
                    : ''
                }`}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <Dialog
        open={showSessionExpiredDialog}
        sx={{ maxWidth: 444, margin: 'auto', zIndex: 1500 }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 28,
            lineHeight: '40px',
            pt: 8,
            px: 5,
          }}
        >
          {texts.SESSION_EXPIRED_TITLE}
          <IconButton
            onClick={() => {
              closeSnackbar();
              localStorage.removeItem('last_selected_api_key');
              navigate('/edit', { replace: true });
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 5, pb: 5 }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 18,
              lineHeight: '25px',
              letterSpacing: 0,
            }}
            variant="body1"
          >
            {texts.SESSION_EXPIRED_DESC}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 7, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              closeSnackbar();
              localStorage.removeItem('last_selected_api_key');
              navigate('/edit', { replace: true });
            }}
            sx={{
              height: 49,
              borderRadius: '4px',
              bgcolor: 'primary.main',
              px: 4,
              fontWeight: 500,
              fontSize: 16,
            }}
          >
            {texts.SESSION_EXPIRED_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
          setPendingUrl(null);
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, lineHeight: '40px' }}>
          {texts.UNSAVED_CHANGES_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontWeight: 400, fontSize: 16, lineHeight: '25px' }}
            variant="body1"
          >
            {texts.UNSAVED_CHANGES_DESC}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowUnsavedDialog(false);
              closeSnackbar();
              allowNavigationRef.current = true;
              if (pendingUrl) {
                window.location.href = pendingUrl;
              } else {
                onClose();
              }
            }}
            sx={{
              width: 150,
              height: 49,
              borderRadius: '4px',
              borderColor: (t: any) => t.custom.outlinedBorder,
              color: 'primary.main',
            }}
          >
            {texts.DISCARD}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setShowUnsavedDialog(false);
              await handlePublish();
            }}
            sx={{
              width: 150,
              height: 49,
              borderRadius: '4px',
              bgcolor: 'primary.main',
            }}
          >
            {keyIsActive ? texts.SAVE_CHANGES : texts.PUBLISH}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export { InfoSnackbar, SuccessSnackbar, WarningSnackbar };
export default EditApiKeyForm;
